import type { RegisterCustomerFormValues } from './types';

/**
 * The checks a registration passes before it is sent, shared by the registration page and the
 * last step of the quotation form (ADR 0008). The backend applies the same rules again.
 */
export type RegistrationFieldErrors = Partial<Record<keyof RegisterCustomerFormValues, string>>;

export const emptyRegistration: RegisterCustomerFormValues = {
  fullName: '',
  email: '',
  phone: '',
  password: '',
};

export function isValidPhoneInput(value: string): boolean {
  const compact = value.trim().replace(/[\s.\-()]/g, '');

  if (compact.startsWith('+')) return /^\+[1-9]\d{7,14}$/.test(compact);
  if (compact.startsWith('00')) return /^[1-9]\d{7,14}$/.test(compact.slice(2));

  const national = compact.startsWith('0') ? compact.slice(1) : compact;
  return /^\d{8,12}$/.test(national);
}

export function validateRegistration(values: RegisterCustomerFormValues): RegistrationFieldErrors {
  const errors: RegistrationFieldErrors = {};

  if (!values.fullName.trim()) errors.fullName = 'Ingresá tu nombre y apellido.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = 'Ingresá un email válido.';
  }
  if (!values.phone.trim()) {
    errors.phone = 'Ingresá un teléfono de contacto.';
  } else if (!isValidPhoneInput(values.phone)) {
    errors.phone = 'Ingresá un teléfono válido, con código de país si no es uruguayo.';
  }
  const password = passwordError(values.password);
  if (password) errors.password = password;

  return errors;
}

/** The password rules of the backend, shared by every page where a password is chosen. */
export function passwordError(password: string): string | undefined {
  if (Array.from(password).length < 10) return 'La contraseña debe tener al menos 10 caracteres.';
  if (!/\p{Lu}/u.test(password)) return 'Agregá al menos una mayúscula.';
  if (!/\p{N}/u.test(password)) return 'Agregá al menos un número.';
  if (!/[^\p{L}\p{N}\s]/u.test(password)) return 'Agregá al menos un carácter especial.';
  if (new TextEncoder().encode(password).length > 72) return 'La contraseña es demasiado larga.';
  return undefined;
}

/**
 * The password typed a second time, to catch a typo before it becomes a password nobody knows.
 * Checked here only: the backend receives the password once.
 */
export function passwordConfirmationError(
  password: string,
  confirmation: string,
): string | undefined {
  if (!confirmation) return 'Repetí la contraseña.';
  if (confirmation !== password) return 'Las contraseñas no coinciden.';
  return undefined;
}
